/**
 * Helper functions for Sentry event filtering and PII scrubbing
 */

import { EMAIL_REGEX, SENSITIVE_PATTERNS, SENSITIVE_KEYS, SENSITIVE_URL_PARAMS, IGNORED_ERROR_PATTERNS } from '../config/sentryConstants';

/**
 * Strips sensitive query parameters from a URL string.
 * Returns the URL with matched params replaced by [Filtered].
 * @param {string} url - URL that may contain sensitive query params
 * @returns {string} - Sanitized URL
 */
const scrubUrlParams = (url) => {
  if (!url || typeof url !== 'string') return url;

  try {
    const parsed = new URL(url, 'https://placeholder.invalid');
    let changed = false;
    for (const param of SENSITIVE_URL_PARAMS) {
      if (parsed.searchParams.has(param)) {
        parsed.searchParams.set(param, '[Filtered]');
        changed = true;
      }
    }
    if (!changed) return url;

    // If the original was a relative path, strip the placeholder origin back out
    if (!url.startsWith('http://') && !url.startsWith('https://')) {
      return parsed.pathname + parsed.search + parsed.hash;
    }
    return parsed.toString();
  } catch {
    // Regex fallback for malformed URLs
    return SENSITIVE_URL_PARAMS.reduce(
      (u, param) => u.replace(
        new RegExp(`([?&])${param}=[^&]*`, 'gi'),
        `$1${param}=[Filtered]`
      ),
      url
    );
  }
};

/**
 * Scrubs a single console breadcrumb (message + data).
 */
const scrubConsoleBreadcrumb = (crumb) => {
  if (crumb.message && SENSITIVE_PATTERNS.test(crumb.message)) {
    return {
      ...crumb,
      message: '[Console message filtered - contained sensitive data]'
    };
  }

  if (crumb.data && typeof crumb.data === 'object') {
    const scrubbedData = {};
    for (const key in crumb.data) {
      if (SENSITIVE_PATTERNS.test(key) || SENSITIVE_PATTERNS.test(String(crumb.data[key]))) {
        scrubbedData[key] = '[Filtered]';
      } else {
        scrubbedData[key] = crumb.data[key];
      }
    }
    return { ...crumb, data: scrubbedData };
  }

  return crumb;
};

/**
 * Scrubs a navigation breadcrumb — strips sensitive query params from
 * the "from" and "to" URLs stored in crumb.data.
 */
const scrubNavigationBreadcrumb = (crumb) => {
  if (!crumb.data) return crumb;

  const scrubbed = { ...crumb, data: { ...crumb.data } };
  if (scrubbed.data.from) scrubbed.data.from = scrubUrlParams(scrubbed.data.from);
  if (scrubbed.data.to) scrubbed.data.to = scrubUrlParams(scrubbed.data.to);
  return scrubbed;
};

/**
 * Scrubs an HTTP / fetch / xhr breadcrumb — strips sensitive query params
 * from the request URL.
 */
const scrubHttpBreadcrumb = (crumb) => {
  if (!crumb.data) return crumb;

  const scrubbed = { ...crumb, data: { ...crumb.data } };
  if (scrubbed.data.url) scrubbed.data.url = scrubUrlParams(scrubbed.data.url);
  return scrubbed;
};

/**
 * Scrubs email addresses from a string
 * @param {string} text - Text that may contain emails
 * @returns {string} - Text with emails replaced
 */
const scrubEmails = (text) => {
  if (!text) return text;
  return text.replace(EMAIL_REGEX, '[email filtered]');
};

/**
 * Scrubs sensitive data from error messages
 * @param {Object} event - Sentry event object
 * @returns {Object} - Modified event
 */
const scrubErrorMessages = (event) => {
  if (event.message) {
    event.message = scrubEmails(event.message);
  }
  return event;
};

/**
 * Scrubs sensitive data from exception values
 * @param {Object} event - Sentry event object
 * @returns {Object} - Modified event
 */
const scrubExceptions = (event) => {
  if (event.exception?.values) {
    event.exception.values = event.exception.values.map(exception => {
      if (exception.value) {
        exception.value = scrubEmails(exception.value);
      }
      return exception;
    });
  }
  return event;
};

/**
 * Scrubs sensitive keys from an object
 * @param {Object} data - Object to scrub
 * @returns {Object} - Scrubbed object
 */
const scrubSensitiveKeys = (data) => {
  if (!data || typeof data !== 'object') return data;

  for (const key of SENSITIVE_KEYS) {
    if (data[key]) {
      data[key] = '[Filtered]';
    }
  }
  return data;
};

/**
 * Scrubs sensitive data from request data
 * @param {Object} event - Sentry event object
 * @returns {Object} - Modified event
 */
const scrubRequestData = (event) => {
  if (event.request?.data && typeof event.request.data === 'object') {
    event.request.data = scrubSensitiveKeys(event.request.data);
  }
  return event;
};

/**
 * Scrubs sensitive data from extra context
 * @param {Object} event - Sentry event object
 * @returns {Object} - Modified event
 */
const scrubExtraContext = (event) => {
  if (event.extra) {
    event.extra = scrubSensitiveKeys(event.extra);
  }
  return event;
};

/**
 * Creates the beforeBreadcrumb function for Sentry.
 * Scrubs sensitive data from each breadcrumb at capture time.
 *
 * @returns {Function} - beforeBreadcrumb function for Sentry configuration
 */
export const createSentryBeforeBreadcrumb = () => {
  return (breadcrumb) => {
    switch (breadcrumb.category) {
      case 'console':
        return scrubConsoleBreadcrumb(breadcrumb);
      case 'navigation':
        return scrubNavigationBreadcrumb(breadcrumb);
      case 'fetch':
      case 'xhr':
      case 'http':
        return scrubHttpBreadcrumb(breadcrumb);
      default:
        return breadcrumb;
    }
  };
};

/**
 * Checks whether a Sentry event matches any of the ignored transient
 * network / browser error patterns.
 * @param {Object} event - Sentry event object
 * @returns {boolean} - True if the event should be dropped
 */
const isIgnoredError = (event) => {
  const message = event.message || '';
  const exceptionValues = event.exception?.values || [];

  for (const pattern of IGNORED_ERROR_PATTERNS) {
    if (pattern.test(message)) return true;
    for (const ex of exceptionValues) {
      if (pattern.test(ex.value || '') || pattern.test(ex.type || '')) return true;
    }
  }
  return false;
};

/**
 * Creates the beforeSend function for Sentry
 * Drops transient network errors and scrubs PII from events
 * 
 * @returns {Function} - beforeSend function for Sentry configuration
 */
export const createSentryBeforeSend = () => {
  return (event, hint) => {
    if (isIgnoredError(event)) {
      return null;
    }

    event = scrubErrorMessages(event);
    event = scrubExceptions(event);
    event = scrubRequestData(event);
    event = scrubExtraContext(event);

    return event;
  };
};
